import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { SignalResponse } from '../../../api/types';
import { X, Calendar, CheckCircle, Hash, Trash2, Edit2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../../../context/UserContext';
import { useWorkspace } from '../../../context/WorkspaceContext';
import signalApi from '../../../api/signalApi';
import { getSignalGroupKey } from '../signalGrouping';
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip } from 'recharts';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";

interface SignalDrawerProps {
    signal: SignalResponse | null;
    open: boolean;
    onClose: () => void;
    onStatusChange?: (id: string, status: 'open' | 'closed' | 'archived') => void;
    onDelete?: (id: string) => void;
    onDateChange?: (id: string, date: Date | null) => void;
    onEdit?: (signal: SignalResponse) => void;
}

const SignalDrawer: React.FC<SignalDrawerProps> = ({ signal, open, onClose, onStatusChange, onDelete, onDateChange, onEdit }) => {
    const navigate = useNavigate();
    const { isAdmin } = useUser();
    const { activeBusinessId } = useWorkspace();
    const embeddedEvidenceRows = Array.isArray(signal?.metadata?.evidence_rows)
        ? (signal?.metadata?.evidence_rows as Record<string, any>[])
        : [];
    const hasEmbeddedEvidence = embeddedEvidenceRows.length > 0;
    const detectorId = String(signal?.metadata?.detector_id || '');
    const shouldFetchApSummaryFallback =
        open &&
        !!activeBusinessId &&
        !hasEmbeddedEvidence &&
        detectorId.startsWith('signal_rule:ap_');
    const { data: weeklySummaryFallback } = useQuery({
        queryKey: ['signal-drawer-weekly-summary', activeBusinessId, signal?._id],
        queryFn: () => signalApi.getWeeklySummary(activeBusinessId || undefined),
        enabled: shouldFetchApSummaryFallback,
        staleTime: 30_000,
    });
    const evidenceRows = React.useMemo<Record<string, any>[]>(
        () => {
            const normalizeRows = (rows: Record<string, any>[]) =>
                rows.filter((row) => {
                    const hasLabel = Boolean(
                        row.reference_label ||
                        row.reference_no ||
                        row.name ||
                        row.vendor_name ||
                        row.customer_name ||
                        row.driver ||
                        row.vendor ||
                        row.source_id ||
                        row.ap_bill_id ||
                        row.invoice_id
                    );
                    const hasAmount = Number.isFinite(Number(row.open_amount ?? row.amount_due ?? row.amount));
                    const hasDate = Boolean(row.due_date || row.due_on || row.event_date);
                    return hasLabel || hasAmount || hasDate;
                });
            if (embeddedEvidenceRows.length > 0) {
                return normalizeRows(embeddedEvidenceRows);
            }
            const fallbackRows = weeklySummaryFallback?.ap_summary?.top_bills;
            return Array.isArray(fallbackRows) ? normalizeRows(fallbackRows as Record<string, any>[]) : [];
        },
        [embeddedEvidenceRows, weeklySummaryFallback?.ap_summary?.top_bills],
    );
    const [evidencePage, setEvidencePage] = React.useState(0);
    const evidencePageSize = 5;

    React.useEffect(() => {
        setEvidencePage(0);
    }, [signal?._id, open]);

    const formatCurrency = (value: unknown) => {
        const amount = typeof value === 'number'
            ? value
            : Number(String(value ?? '').replace(/[$,\s]/g, ''));
        if (!Number.isFinite(amount)) return '—';
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(amount);
    };

    const buildEvidenceTargetLink = (row: Record<string, any>): string => {
        const direct = String(row.action_link || '').trim();
        if (direct.startsWith('/ledger?') || direct.startsWith('/?') || direct.startsWith('/margin')) {
            return direct;
        }

        const vendor = String(row.vendor_name || row.customer_name || row.vendor || row.driver || '').trim();
        const search = String(row.reference_label || row.reference_no || row.name || '').trim();
        const detector = String(signal?.metadata?.detector_id || '');
        const signalType = String(signal?.type || '').trim().toLowerCase();
        const hasVendorOnlyEvidence = Boolean(row.ap_vendor_id) && !row.ap_bill_id && !row.invoice_id;

        const params = new URLSearchParams();
        if (detector.startsWith('ar_signal:') || signalType === 'ar' || row.invoice_id || row.payment_id) {
            params.set('view', 'ar');
            params.set('source', row.invoice_id || row.payment_id ? 'ar_ledger' : 'ar_customers');
            if (vendor) params.set('search', vendor);
            if (search && !vendor) params.set('search', search);
            return `/ledger?${params.toString()}`;
        }

        if (detector.startsWith('signal_rule:ap_') || signal?.type === 'bill' || signal?.type === 'invoice') {
            params.set('view', 'ar');
            params.set('source', hasVendorOnlyEvidence ? 'ap_vendors' : 'ap_ledger');
            if (vendor) params.set('search', vendor);
            if (search && !vendor) params.set('search', search);
            return `/ledger?${params.toString()}`;
        }

        params.set('view', 'cash');
        if (vendor) {
            params.set('vendor_name', vendor);
            params.set('vendor_exact', 'true');
        }
        if (search && !vendor) params.set('search', search);
        if (String(row.item_label || '').toLowerCase().includes('expected payment')) {
            params.set('direction', 'outflow');
            params.set('operating_flag', 'true');
        }
        return `/ledger?${params.toString()}`;
    };

    const formatDate = (value: unknown) => {
        if (!value) return '—';
        const date = new Date(String(value));
        if (Number.isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };

    const evidencePageCount = Math.max(1, Math.ceil(evidenceRows.length / evidencePageSize));
    const pagedEvidenceRows = evidenceRows.slice(
        evidencePage * evidencePageSize,
        evidencePage * evidencePageSize + evidencePageSize,
    );
    const reasoningSummary = String(signal?.metadata?.reasoning_summary || '').trim();
    const sourceCollections = Array.isArray(signal?.metadata?.source_collections)
        ? (signal?.metadata?.source_collections as string[]).filter(Boolean)
        : [];
    const debugEntries = Object.entries((signal?.metadata?.debug as Record<string, unknown>) || {}).filter(([, value]) => {
        if (value == null) return false;
        if (Array.isArray(value)) return value.length > 0;
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
    });

    if (!open) return null;

    const getActionLink = (currentSignal: SignalResponse | null) => {
        if (!currentSignal) return '/ledger';
        switch (getSignalGroupKey(currentSignal)) {
            case 'margin': return '/margin';
            case 'cash': return '/';
            case 'ap': return '/ledger?view=ap';
            case 'ar': return '/ledger?view=ar';
            case 'data': return '/ledger?status=needs_review';
            default: return '/ledger';
        }
    };

    return (
        <>
            <div
                className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-40 transition-opacity"
                onClick={onClose}
            />

            <div className={`fixed inset-y-0 right-0 w-full sm:w-[480px] max-w-full bg-white shadow-2xl z-50 transform transition-transform duration-300 ease-in-out ${open ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}>

                {/* Top Utility Bar */}
                {/* <div className="flex justify-end items-center gap-6 px-8 py-3 text-gray-400">
                    <button className="hover:text-gray-600 transition-colors pointer-events-none opacity-40"><Download size={18} /></button>
                    <button className="hover:text-gray-600 transition-colors pointer-events-none opacity-40"><Share2 size={18} /></button>
                    <button className="hover:text-gray-600 transition-colors pointer-events-none opacity-40"><Plus size={18} /></button>
                </div> */}

                {/* Header Section */}
                <div className="px-8 pt-8 pb-6 flex flex-col">
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="text-lg font-bold text-gray-800">Signal Details</h2>
                        <div className="flex items-center gap-3">
                            {isAdmin && (
                                <button 
                                    onClick={() => { if(signal && onEdit) onEdit(signal) }} 
                                    className="text-gray-500 hover:text-indigo-600 transition-colors flex items-center gap-1.5 text-xs font-semibold bg-gray-50 hover:bg-indigo-50 px-2.5 py-1.5 rounded-md border border-gray-200"
                                >
                                    <Edit2 size={13} /> Edit
                                </button>
                            )}
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                    </div>
                    <div className="border-b border-gray-100 mb-6" />

                    <div className="space-y-4">
                        <div>
                            <span className={`inline-block mb-4 rounded px-3 py-1 text-[11px] font-bold tracking-wide ${signal?.priority === 1 ? 'bg-red-50 text-red-400' :
                                signal?.priority === 2 ? 'bg-orange-50 text-orange-400' :
                                    'bg-yellow-50 text-yellow-400'
                                }`}>
                                {signal?.priority === 1 ? 'High' : signal?.priority === 2 ? 'Medium' : 'Low'}
                            </span>
                            <h1 className="text-2xl font-bold text-gray-800 leading-tight mb-2">
                                {signal?.title}
                            </h1>
                            <p className="text-[14px] text-gray-500 font-medium">
                                {signal?.impact}
                            </p>
                        </div>

                        <div className="flex gap-8 text-[11px] font-bold tracking-widest text-gray-500 mt-2">
                            <div className="flex items-center gap-2">
                                DATE : <span className="text-gray-400">{signal?.created_at ? new Date(signal.created_at).toLocaleDateString() : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                DUE: <div className="inline-block">
                                    <DatePicker
                                        selected={signal?.due_on ? new Date(signal.due_on) : null}
                                        onChange={(date: Date | null) => {
                                            if (signal?._id && onDateChange) {
                                                onDateChange(signal._id, date);
                                            }
                                        }}
                                        customInput={
                                            <div className="flex items-center gap-1 cursor-pointer hover:text-indigo-600 transition-colors">
                                                <span className="text-gray-400">{signal?.due_on ? new Date(signal.due_on).toLocaleDateString() : 'N/A'}</span>
                                                <Calendar size={14} className="text-gray-400" />
                                            </div>
                                        }
                                        popperPlacement="bottom-end"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <button
                                onClick={() => {
                                    if (signal?.action_link) {
                                        navigate(signal.action_link);
                                        onClose();
                                        return;
                                    }
                                    if (onStatusChange && signal?._id) onStatusChange(signal._id, 'closed');
                                    onClose();
                                }}
                                className="flex items-center justify-center gap-2 py-3 border border-gray-200 rounded-xl text-gray-500 font-semibold hover:bg-gray-50 transition-all text-sm"
                            >
                                <CheckCircle size={18} className="text-gray-400" />
                                {signal?.action_link ? 'Open Source' : 'Resolve'}
                            </button>
                            <button
                                onClick={() => {
                                    if (signal?._id && onDelete) onDelete(signal._id);
                                    onClose();
                                }}
                                className="flex items-center justify-center gap-2 py-3 border border-red-200 rounded-xl text-red-600 font-semibold hover:bg-red-50 transition-all text-sm"
                            >
                                <Trash2 size={18} className="text-red-500" />
                                Delete
                            </button>
                        </div>
                    </div>
                </div>

                {/* Content Sections */}
                <div className="flex-1 overflow-y-auto px-8 pb-4 space-y-4 scrollbar-hide">
                    {/* Context Section */}
                    <section>
                        <h3 className="text-[15px] font-bold text-gray-800 mb-2">Context</h3>
                        <p className="text-[14px] text-gray-500 leading-relaxed font-medium">
                            {signal?.details || 'This signal requires your review. Resolving it will ensure your financial projections stay accurate.'}
                        </p>
                    </section>

                    {(reasoningSummary || sourceCollections.length > 0 || debugEntries.length > 0) && (
                        <>
                            <div className="border-b border-gray-50" />
                            <section className="space-y-4">
                                {reasoningSummary && (
                                    <div>
                                        <h3 className="text-[15px] font-bold text-gray-800 mb-2">How Calculated</h3>
                                        <p className="text-[14px] text-gray-500 leading-relaxed font-medium">
                                            {reasoningSummary}
                                        </p>
                                    </div>
                                )}
                                {sourceCollections.length > 0 && (
                                    <div>
                                        <h4 className="text-[13px] font-bold uppercase tracking-wide text-gray-500 mb-2">Sources</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {sourceCollections.map((source) => (
                                                <span key={source} className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-600">
                                                    {source}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                {debugEntries.length > 0 && (
                                    <div>
                                        <h4 className="text-[13px] font-bold uppercase tracking-wide text-gray-500 mb-2">Debug</h4>
                                        <div className="rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                                            <div className="space-y-2 text-xs text-gray-600">
                                                {debugEntries.slice(0, 8).map(([key, value]) => (
                                                    <div key={key} className="flex items-start justify-between gap-3">
                                                        <span className="font-semibold text-gray-500">{key}</span>
                                                        <span className="text-right">
                                                            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>
                        </>
                    )}

                    <div className="border-b border-gray-50" />

                    {/* Projected Outcome Section */}
                    <section>
                        <h3 className="text-[15px] font-bold text-gray-800 mb-2">Projected Outcome</h3>
                        <p className="text-[14px] text-gray-500 leading-relaxed font-medium mb-8">
                            {signal?.projected_outcome || 'Resolving this item will officially update the forecast and extend your operational runway.'}
                        </p>

                        {evidenceRows.length > 0 && (
                            <div className="mb-8">
                                <div className="mb-3 flex items-center justify-between gap-3">
                                    <h4 className="text-[13px] font-bold uppercase tracking-wide text-gray-500">Relevant Items</h4>
                                    {evidenceRows.length > evidencePageSize && (
                                        <div className="flex items-center gap-2 text-xs font-medium text-gray-500">
                                            <button
                                                type="button"
                                                onClick={() => setEvidencePage((page) => Math.max(0, page - 1))}
                                                disabled={evidencePage === 0}
                                                className="rounded border border-gray-200 px-2 py-1 text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                                Prev
                                            </button>
                                            <span>{evidencePage + 1} / {evidencePageCount}</span>
                                            <button
                                                type="button"
                                                onClick={() => setEvidencePage((page) => Math.min(evidencePageCount - 1, page + 1))}
                                                disabled={evidencePage >= evidencePageCount - 1}
                                                className="rounded border border-gray-200 px-2 py-1 text-gray-600 disabled:cursor-not-allowed disabled:opacity-40"
                                            >
                                                Next
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <div className="overflow-hidden rounded-lg border border-gray-100">
                                    <div className="divide-y divide-gray-100">
                                        {pagedEvidenceRows.map((row: Record<string, any>, index: number) => {
                                            const reference =
                                                row.reference_label ||
                                                row.reference_no ||
                                                row.name ||
                                                row.vendor_name ||
                                                row.customer_name ||
                                                row.driver ||
                                                row.vendor ||
                                                row.source_id ||
                                                row.ap_bill_id ||
                                                row.invoice_id ||
                                                `Item ${index + 1}`;
                                            const secondaryLabel = row.vendor_name || row.customer_name || row.driver || row.vendor || null;
                                            const accountLabel = String(row.accounting_account_name || row.account || '').trim() || null;
                                            const rawItemLabel = String(row.item_label || '').trim();
                                            const obligationSource = String(row.obligation_source || row.source_type || '').trim().toLowerCase();
                                            const isRecurringLike =
                                                obligationSource.includes('recurring') ||
                                                obligationSource.includes('expected payment') ||
                                                obligationSource.includes('obligation');
                                            const itemType =
                                                rawItemLabel
                                                    ? (
                                                        /expected payment/i.test(rawItemLabel)
                                                            ? 'Expected Payment'
                                                            : /obligation/i.test(rawItemLabel)
                                                                ? 'Obligation'
                                                                : /bill/i.test(rawItemLabel) && isRecurringLike
                                                                    ? 'Expected Payment'
                                                                    : rawItemLabel
                                                    )
                                                    : (row.ap_vendor_id && !row.ap_bill_id && !row.invoice_id
                                                    ? 'Vendor'
                                                    : row.obligation_source === 'ap_recurring'
                                                        ? 'Expected Payment'
                                                        : isRecurringLike
                                                            ? 'Expected Payment'
                                                        : 'Bill');
                                            const dueLabel = formatDate(row.due_date || row.due_on || row.event_date);
                                            const amount = formatCurrency(row.open_amount ?? row.amount_due ?? row.amount);
                                            const status = row.status || 'open';
                                            const targetLink = buildEvidenceTargetLink(row) || signal?.action_link || getActionLink(signal || null);
                                            const clickable = Boolean(targetLink);
                                            return (
                                                <button
                                                    key={`${reference}-${index}`}
                                                    type="button"
                                                    disabled={!clickable}
                                                    onClick={() => {
                                                        if (!targetLink) return;
                                                        navigate(targetLink);
                                                        onClose();
                                                    }}
                                                    className={`flex w-full items-start justify-between gap-3 px-4 py-3 text-left ${clickable ? 'hover:bg-gray-50 transition-colors' : ''}`}
                                                >
                                                    <div className="min-w-0">
                                                        <div className="truncate text-sm font-medium text-gray-900">{reference}</div>
                                                        <div className="text-xs text-gray-500">
                                                            {[
                                                                secondaryLabel && secondaryLabel !== reference ? secondaryLabel : null,
                                                                itemType,
                                                                accountLabel,
                                                                dueLabel !== '—' ? dueLabel : null,
                                                                status && status !== 'open' ? status : null,
                                                            ].filter(Boolean).join(' · ')}
                                                        </div>
                                                    </div>
                                                    <div className="shrink-0 text-sm font-semibold text-gray-900">{amount}</div>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Visual Logic */}
                        {signal?.visual?.type === 'sparkline' ? (
                            <div className="w-full h-[240px] relative">
                                <ResponsiveContainer width="100%" height="100%">
                                    <LineChart data={
                                        (signal.visual?.data?.points || []).map((val: number, i: number) => ({
                                            label: signal.visual?.data?.labels?.[i] || `P${i + 1}`,
                                            value: val
                                        }))
                                    }>
                                        <XAxis dataKey="label" hide={false} axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#9ca3af' }} />
                                        <YAxis hide={true} domain={['auto', 'auto']} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)', fontSize: '12px', fontWeight: 500 }}
                                            itemStyle={{ color: '#4f46e5' }}
                                            labelStyle={{ color: '#6b7280', marginBottom: '4px' }}
                                        />
                                        <Line
                                            type="monotone"
                                            dataKey="value"
                                            stroke="#4f46e5"
                                            strokeWidth={2.5}
                                            dot={{ r: 3, fill: '#4f46e5', strokeWidth: 0 }}
                                            activeDot={{ r: 5, fill: '#fff', stroke: '#4f46e5', strokeWidth: 2 }}
                                        />
                                    </LineChart>
                                </ResponsiveContainer>
                            </div>
                        ) : signal?.visual?.type === 'table' ? (
                            <div className="w-full overflow-hidden rounded-lg border border-gray-100">
                                <table className="w-full text-left border-collapse">
                                    <thead>
                                        <tr className="bg-gray-50 border-b border-gray-100">
                                            {(signal.visual?.data?.headers || []).map((header: string, i: number) => (
                                                <th key={header + i} className={`px-4 py-2 text-[10px] font-bold text-gray-400 tracking-wider ${i > 0 ? 'text-right' : ''}`}>
                                                    {header}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-50">
                                        {Array.isArray(signal.visual?.data?.rows) && signal.visual?.data?.rows.map((row: any[], i: number) => (
                                            <tr key={i} className="hover:bg-gray-50 transition-colors">
                                                {Array.isArray(row) && row.map((cell: any, j: number) => (
                                                    <td key={j} className={`px-4 py-3 text-sm ${j === 0 ? 'text-gray-600 font-medium' : 'text-gray-900 font-semibold text-right'}`}>
                                                        {cell}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : evidenceRows.length === 0 ? (
                            <div className="text-sm text-gray-400 italic flex items-center gap-2 py-4 justify-center border border-dashed border-gray-200 rounded-lg">
                                <Hash size={14} /> No visual context available for this signal.
                            </div>
                        ) : null}
                    </section>

                    {/* Footer Info
                    <div className="pt-4 border-t border-gray-50 text-center">
                        <p className="text-[12px] text-gray-400 font-medium tracking-tight">
                            Last edited by System • {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                        </p>
                    </div> */}
                </div>
            </div>
        </>
    );
};

export default SignalDrawer;
